import React from "react";
import { Grid, useMediaQuery } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/rootReducer";
import { loadSummary } from "redux/summary/summaryFunctions";
import { getReportDateSelector } from "redux/user/user";
import Table from "components/Table";
import { Cell } from "react-table";
import { asEur, asPercent } from "utility/numbers";
import { Summary } from "types/Summary";

const SummaryList = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const matchesMediaQuery = useMediaQuery("(min-width: 960px)");

  const reportControlId = useSelector(
    (state: RootState) => state.user.reportControlId
  );
  const reportDate = useSelector(getReportDateSelector);

  const summary = useSelector(
    (state: RootState) => state.summary.all?.data ?? []
  );
  const loading = useSelector((state: RootState) => state.summary.all?.loading);

  React.useEffect(() => {
    if (reportControlId && reportDate) {
      dispatch(loadSummary(reportControlId, reportDate));
    }
  }, [dispatch, reportControlId, reportDate]);

  const summaryIndex = summary.findIndex((x) => x.AssetClass === "Yhteensä");

  const summaryBody = summary.slice(0, summaryIndex);
  const summaryFooter = summary.slice(summaryIndex);

  const summaryColumns = [
    {
      Header: t("current_summary"),
      accessor: "Yhteenveto",
      paddingRight: "10px",
      width: 200,
      minWidth: 170,
      Footer: summaryFooter.map((s, i) => (
        <div
          key={s.AssetClass}
          style={{
            whiteSpace: "pre-wrap",
            padding: "2px 10px 2px 2px",
            borderTop:
              i === summaryFooter.length - 1 ? "3px double #4a76a2" : undefined,
          }}
        >
          {i === summaryFooter.length - 1 && s.Yhteenveto === ""
            ? t("all_included")
            : s.Yhteenveto === ""
            ? " "
            : s.Yhteenveto}
        </div>
      )),
    },
    {
      Header: t("weight"),
      accessor: "Paino",
      minWidth: 70,
      width: 70,
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) =>
        asPercent(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: summaryFooter.map((s, i) => (
        <div
          key={s.AssetClass}
          style={{
            padding: "2px 10px 2px 2px",
            fontFamily:
              i !== summaryFooter.length - 4 ? "Lato Light" : "Lato Regular",
            borderTop:
              i === summaryFooter.length - 1 ? "3px double #4a76a2" : undefined,
          }}
        >
          {asPercent(s.Paino, undefined, true)}
        </div>
      )),
    },
    {
      Header: t("market_value"),
      accessor: "MarkkinaArvo",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => asEur(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: summaryFooter.map((s, i) => (
        <div
          key={s.AssetClass}
          style={{
            padding: "2px 10px 2px 2px",
            fontFamily:
              i !== summaryFooter.length - 4 ? "Lato Light" : "Lato Regular",
            borderTop:
              i === summaryFooter.length - 1 ? "3px double #4a76a2" : undefined,
          }}
        >
          {asEur(s.MarkkinaArvo, undefined, true)}
        </div>
      )),
    },
    {
      Header: t("acquisition_value"),
      accessor: "HankintaArvo",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => asEur(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: summaryFooter.map((s, i) => (
        <div
          key={s.AssetClass}
          style={{
            whiteSpace: "pre-wrap",
            padding: "2px 10px 2px 2px",
            borderTop:
              i === summaryFooter.length - 1 ? "3px double #4a76a2" : undefined,
          }}
        >
          {i === 0 ? asEur(s.HankintaArvo, undefined, true) : " "}
        </div>
      )),
    },
    {
      Header: t("dividend_and_other_profits"),
      accessor: "Tuottokassavirta",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => asEur(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: summaryFooter.map((s, i) => (
        <div
          key={s.AssetClass}
          style={{
            whiteSpace: "pre-wrap",
            padding: "2px 10px 2px 2px",
            borderTop:
              i === summaryFooter.length - 1 ? "3px double #4a76a2" : undefined,
          }}
        >
          {i === 0 ? asEur(s.Tuottokassavirta, undefined, true) : " "}
        </div>
      )),
    },
    {
      Header: t("total_profit"),
      accessor: "TuottoEur",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => asEur(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: summaryFooter.map((s, i) => (
        <div
          key={s.AssetClass}
          style={{
            whiteSpace: "pre-wrap",
            padding: "2px 10px 2px 2px",
            borderTop:
              i === summaryFooter.length - 1 ? "3px double #4a76a2" : undefined,
          }}
        >
          {i === 0 ? asEur(s.TuottoEur, undefined, true) : " "}
        </div>
      )),
    },
    {
      Header: t("total_profit"),
      accessor: "Tuotto",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) =>
        asPercent(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: summaryFooter.map((s, i) => (
        <div
          key={s.AssetClass}
          style={{
            whiteSpace: "pre-wrap",
            padding: "2px 10px 2px 2px",
            borderTop:
              i === summaryFooter.length - 1 ? "3px double #4a76a2" : undefined,
          }}
        >
          {i === 0 ? asPercent(s.Tuotto, undefined, true) : " "}
        </div>
      )),
    },
    {
      Header: t("profit_1m"),
      whiteSpace: "nowrap",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => " ",
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: summaryFooter.map((s, i) => (
        <div
          key={s.AssetClass}
          style={{
            whiteSpace: "pre-wrap",
            padding: "2px 10px 2px 2px",
            borderTop:
              i === summaryFooter.length - 1 ? "3px double #4a76a2" : undefined,
          }}
        >
          {" "}
        </div>
      )),
      hideTitle: true,
    },
    {
      Header: t("profit_3m"),
      whiteSpace: "nowrap",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => " ",
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: summaryFooter.map((s, i) => (
        <div
          key={s.AssetClass}
          style={{
            whiteSpace: "pre-wrap",
            padding: "2px 10px 2px 2px",
            borderTop:
              i === summaryFooter.length - 1 ? "3px double #4a76a2" : undefined,
          }}
        >
          {" "}
        </div>
      )),
      hideTitle: true,
    },
    {
      Header: t("profit_ytd"),
      width: 200,
      minWidth: 150,
      Cell: (cell: Cell<Summary, number>) => " ",
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: summaryFooter.map((s, i) => (
        <div
          key={s.AssetClass}
          style={{
            whiteSpace: "pre-wrap",
            padding: "2px 10px 2px 2px",
            borderTop:
              i === summaryFooter.length - 1 ? "3px double #4a76a2" : undefined,
          }}
        >
          {" "}
        </div>
      )),
      hideTitle: true,
    },
  ];

  return (
    <Grid
      item
      sm={12}
      style={{
        padding: matchesMediaQuery ? "0 0 16px 0" : "0 16px 16px 16px",
        overflowY: "hidden",
      }}
    >
      <Table
        data={summaryBody}
        columns={summaryColumns}
        loading={loading}
        hasFooter
        footerStyles={{ padding: 0 }}
      />
    </Grid>
  );
};

export default React.memo(SummaryList);
